import { DOCUMENT } from "@angular/common";
import { NbComponentSize, NbComponentStatus, NbMenuItem } from "@nebular/theme";
import { Component, Input, Inject } from "@angular/core";
import { defer, interval, Observable, timer } from "rxjs";
import { delayWhen, filter, map, retryWhen, take } from "rxjs/operators";

@Component({
    selector: "kno2-split-button",
    templateUrl: "./split-button.component.html",
    styleUrls: ["./split-button.component.scss"],
    standalone: false
})
export class SplitButtonComponent {
    @Input() public status: NbComponentStatus = "basic";
    @Input() public size: NbComponentSize = "medium";
    @Input() public classList: Array<string> = [];
    @Input() public menu: {
        tag: string;
        items: Array<NbMenuItem>;
    };
    @Input() public menuClickCallback?: (item: NbMenuItem) => void;
    private menuEl: Element = null;
    private menuOpen = false;

    constructor(@Inject(DOCUMENT) private document: Document) {}

    /**
     * Registers an onClick handler with the menu children
     * @param e
     */
    public onMenuClick(e: any): void {
        this.menuOpen = !this.menuOpen;

        if (!this.menuOpen) {
            this.clearEventListeners();
            return;
        }

        defer(() => this.checkForMenuOrRetry$())
            .pipe(take(1))
            .subscribe((menu) => {
                if (!this.menuEl) {
                    this.menuEl = menu;
                    this.menuEl.addEventListener("click", this.onMenuItemClick.bind(this));
                }
            });
    }

    private onMenuItemClick(e: Event): void {
        const elementText = (e.target as HTMLElement).innerHTML as string;
        const item = this.menu.items.find((x) => x.title === elementText);

        if (item) this.menuClickCallback(item);
    }

    public onMenuBlur(): void {
        interval(10)
            .pipe(take(1))
            .subscribe(() => this.clearEventListeners());
    }

    private checkForMenuOrRetry$(retryDelay = 5): Observable<Element> {
        // getting the element dynamically because it is only
        // inserted into the dom when the menu button is clicked
        return interval(retryDelay).pipe(
            map(() => {
                const menus = this.document.getElementsByTagName("nb-menu");
                let menu: Element = null;

                for (let i = 0; i < menus.length; i++) {
                    const item = menus.item(i);
                    if (item.classList.contains("context-menu")) menu = item;
                }

                if (!menu) throw Error();
                return menu;
            }),
            retryWhen((err) => err.pipe(delayWhen(() => timer(retryDelay)))),
            filter((element) => !!element)
        );
    }

    private clearEventListeners(): void {
        this.menuEl?.removeEventListener("click", this.onMenuItemClick.bind(this));
        this.menuEl = null;
        this.menuOpen = false;
    }
}
